import type { CustomLayerInterface, CustomRenderMethodInput, Map as MapLibreMap } from 'maplibre-gl'

import type { Layer } from '../layer'
import type { GrayImage } from './plate'
import { decodePlate } from './plateDecoder'
import { nearestIndex } from './timeSource'

/**
 * The way a model field is drawn: a custom WebGL layer that uploads a **value**
 * texture and applies the ramp in a fragment shader.
 *
 * It keeps one texture and swaps it in place, warming the frames around the
 * cursor into the HTTP cache — which is what `ImageFramesLayer` did, because an
 * `image` source has no viewport culling and neither does a full-plate quad.
 * That layer is gone; this replaced it.
 *
 * **Three things the `raster` layer could not do at any price**, which is why:
 *
 * * **Colour is applied at draw time**, so editing a colormap takes effect on
 *   the next page load with no re-render. That is the argument that decided
 *   replacing rather than coexisting.
 * * **The filter interpolates values, not colours.** MapLibre's
 *   `raster-resampling: 'linear'` interpolates the *paletted image*, and our
 *   ramps run cyan→blue→green→yellow→red→magenta, so 66.8% of opaque pixels
 *   came out a colour the ramp never produces. That, not smoothing, is why
 *   every field used to draw `nearest`.
 * * **Alpha is decided after the interpolation.** The four samples are weighted
 *   by *validity*, and the coverage they sum to becomes the alpha — so the
 *   plate fades out over one texel at its own domain edge instead of ending in
 *   a staircase, and nothing ever blends toward whatever colour sits behind a
 *   transparent entry. A fifth of a reflectivity plate's drawn pixels sit on an
 *   alpha boundary (measured), which is why this is the whole ballgame rather
 *   than a polish item.
 *
 * **Every field draws smooth**, and there is no per-field filter left to state.
 * The old rule had three conditions — bilinear warp, no floor, no sentinel —
 * and the last two existed only because a *hardware* filter blends colour and
 * alpha together. A validity-weighted interpolation in value space has neither
 * hazard, so `refc` and `apcp1h` are smooth too. What that spends is written
 * down where somebody will find it: `formatReadout` in `probe.ts`, because
 * between cell centres the colour is now a blend and `/api/point` still
 * returns the cell.
 *
 * **Every GL call lives inside `onAdd`, `render` and `onRemove`**, including
 * the texture upload. MapLibre wraps the context in its own `Context` object
 * that remembers what it last set, and calls `setCustomLayerDefaults()` before
 * a custom layer and `setDirty()` after — so anything done *inside* `render`
 * is safe and anything done outside it writes real GL state behind a cache
 * that still believes its own value, with the corruption surfacing in somebody
 * else's layer. The cost is that a texture is uploaded on the frame after its
 * decode lands, which is 16 ms and invisible. It also keeps the layer
 * mountable under vitest's fake map, which has no GL at all.
 *
 * **There is no source**, and that has three consequences: no attribution
 * (MapLibre's `AttributionControl` reads sources in use, so the credit moved
 * into `Legend.svelte`), no `queryRenderedFeatures` (irrelevant — a model
 * readout was never feature-based; it asks `/api/point` and still does), and
 * nothing for MapLibre to restore after a lost WebGL context. MapLibre says so
 * itself, in a console warning naming the layer. `LayerController` re-adds
 * every layer on `style.load` and a context restore fires exactly that event,
 * which is why this is mounted through the controller like the other six
 * kinds rather than bolted onto the map.
 */

export interface TextureFrame {
  /** The instant this frame is valid for. */
  time: Date
  url: string
}

/** MapLibre's corner order: top-left, top-right, bottom-right, bottom-left. */
export type Corners = [[number, number], [number, number], [number, number], [number, number]]

/** The plate's box in EPSG:3857 metres — west, south, east, north. */
export type Bbox3857 = [number, number, number, number]

export interface DataTextureSpec {
  frames: TextureFrame[]
  bbox3857: Bbox3857
  /** `(levels * 3)` RGB bytes from `ramp.ts`, across the run's own range. */
  ramp: Uint8Array
  /**
   * The first index that means "nothing here": it and every index above it
   * draw nothing. 255 for a model plate, from the manifest; the radar map
   * keeps 254 and 255 apart (beyond the radars, dry) for its readout.
   */
  nodata: number
  /**
   * The first index of a second class the filter must never blend across —
   * snow, on the radar map, where the mean of a rain index and a snow index is
   * a colour that means nothing. Absent means one class, which is every model
   * plate, and the shader then does exactly what it did before.
   */
  split?: number
  attribution?: string
}

export interface DataTextureOptions {
  id: string
  attribution: string
  opacity: number
  /**
   * Resolve the frames, the box they go in and how to colour them. Must
   * resolve rather than reject: a layer that cannot load its frames degrades
   * to drawing nothing, which is the contract `Layer.load()` states.
   */
  loadFrames?(): Promise<DataTextureSpec | null>
  /** How many frames either side of the cursor to warm into the HTTP cache. */
  prefetchAhead?: number
  prefetchBehind?: number
}

const DEFAULT_AHEAD = 3
const DEFAULT_BEHIND = 1

/** Half the width of the Mercator world, in metres. */
const HALF_WORLD = 20037508.342789244

/**
 * The plate's corners in MapLibre's mercator space — `[0, 0]` top-left of the
 * world, `[1, 1]` bottom-right — which is what `defaultProjectionData.mainMatrix`
 * expects.
 *
 * Straight out of `bbox3857` rather than through `MercatorCoordinate.fromLngLat`
 * on the manifest's `coordinates`. The two agree, because `coordinates` *is*
 * that box transformed to WGS84 — but this way there is no round trip through
 * lon/lat and no runtime import of MapLibre, which is what keeps this file
 * mountable under vitest's fake map.
 *
 * Returned in MapLibre's own corner order so the UV mapping beside it reads
 * the same way the image source's `coordinates` did.
 */
export function mercatorQuad(bbox: Bbox3857): Corners {
  const [west, south, east, north] = bbox
  const x = (metres: number) => 0.5 + metres / (2 * HALF_WORLD)
  const y = (metres: number) => 0.5 - metres / (2 * HALF_WORLD)
  return [
    [x(west), y(north)],
    [x(east), y(north)],
    [x(east), y(south)],
    [x(west), y(south)],
  ]
}

/**
 * Where a sample lands, in texel-centre space, and which four texels it weighs.
 *
 * **The convention is that cell `(c, r)` sits at `(c, r)`** — the same
 * convention `app/models/contour.py` writes down for the same reason, and the
 * same class of mistake if it is got wrong. `p = uv * size - 0.5` is what puts
 * a texel's *centre* at its integer coordinate; `i` is then the texel
 * below-left of the sample point, which is emphatically **not** the nearest
 * one. Getting that half texel wrong is 1.4 km on an HRRR plate, in the same
 * direction everywhere, and looks entirely plausible on a map.
 *
 * This is the TypeScript mirror the shader cannot be called from, and
 * `dataTexture.test.ts` is what stops the two drifting: it checks that a value
 * at a texel's centre reads back as exactly that texel and that the weights
 * sum to one, and greps the shader source for the `texelFetch` and
 * validity-weighting forms that make the filter honest.
 */
export function sampleWeights(
  u: number,
  v: number,
  width: number,
  height: number,
): { texels: [number, number][]; weights: number[] } {
  const px = u * width - 0.5
  const py = v * height - 0.5
  const ix = Math.floor(px)
  const iy = Math.floor(py)
  const fx = px - ix
  const fy = py - iy
  const texels: [number, number][] = []
  const weights: number[] = []
  for (let dy = 0; dy < 2; dy++) {
    for (let dx = 0; dx < 2; dx++) {
      texels.push([
        Math.min(width - 1, Math.max(0, ix + dx)),
        Math.min(height - 1, Math.max(0, iy + dy)),
      ])
      weights.push((dx === 0 ? 1 - fx : fx) * (dy === 0 ? 1 - fy : fy))
    }
  }
  return { texels, weights }
}

const VERTEX_SOURCE = `#version 300 es
in vec2 a_pos;
in vec2 a_uv;
uniform mat4 u_matrix;
out vec2 v_uv;
void main() {
  v_uv = a_uv;
  gl_Position = u_matrix * vec4(a_pos, 0.0, 1.0);
}`

/**
 * The sampling half of the fragment shader, kept separate so a test can grep
 * it for the one line that has a plausible-looking wrong form.
 *
 * `texelFetch`, not `texture`: hardware `LINEAR` would blend the nodata index
 * into its neighbours at every domain edge and produce a value between the top
 * of the ramp and nothing. Writing the filter out is what lets validity be part
 * of it.
 */
export const SAMPLE_GLSL = `
  vec2 p = v_uv * vec2(u_size) - 0.5;
  ivec2 i = ivec2(floor(p));
  vec2 f = fract(p);

  float sum = 0.0;
  float cover = 0.0;
  float sumB = 0.0;
  float coverB = 0.0;
  for (int dy = 0; dy < 2; ++dy) {
    for (int dx = 0; dx < 2; ++dx) {
      ivec2 c = clamp(i + ivec2(dx, dy), ivec2(0), u_size - 1);
      float value = texelFetch(u_data, c, 0).r * 255.0;
      float w = (dx == 0 ? 1.0 - f.x : f.x) * (dy == 0 ? 1.0 - f.y : f.y);
      if (value < u_nodata - 0.5) {
        if (value < u_split - 0.5) {
          sum += w * value;
          cover += w;
        } else {
          sumB += w * value;
          coverB += w;
        }
      }
    }
  }`

/**
 * Two classes, never blended: whichever holds more of the four weights wins
 * the texel's colour, and the two together are its coverage. With no split
 * (\`u_split\` at \`u_nodata\`) the second class is always empty and this is
 * the one-class filter unchanged. \`blendClasses\` is the TypeScript mirror.
 */
export const CLASS_GLSL = `
  if (coverB > 0.0) {
    float total = cover + coverB;
    float mean = coverB > cover ? sumB / coverB : sum / cover;
    sum = mean * total;
    cover = total;
  }`

/**
 * The texel's index and coverage from four samples, as the shader works it
 * out: the samples at or above \`nodata\` count for nothing, and those either
 * side of \`split\` are two classes of which the heavier wins. Null where
 * nothing covers the point. The mirror of \`SAMPLE_GLSL\` + \`CLASS_GLSL\`,
 * which \`dataTexture.test.ts\` keeps from drifting.
 */
export function blendClasses(
  values: readonly number[],
  weights: readonly number[],
  nodata: number,
  split = nodata,
): { index: number; cover: number } | null {
  let sum = 0
  let cover = 0
  let sumB = 0
  let coverB = 0
  values.forEach((value, i) => {
    if (value >= nodata) return
    if (value < split) {
      sum += weights[i] * value
      cover += weights[i]
    } else {
      sumB += weights[i] * value
      coverB += weights[i]
    }
  })
  const total = cover + coverB
  if (total < 1e-4) return null
  const mean = coverB > cover ? sumB / coverB : sum / cover
  return { index: mean, cover: total }
}

const FRAGMENT_SOURCE = `#version 300 es
precision highp float;
uniform highp sampler2D u_data;
uniform sampler2D u_ramp;
uniform ivec2 u_size;
uniform float u_nodata;
uniform float u_split;
uniform float u_opacity;
uniform float u_levels;
in vec2 v_uv;
out vec4 fragColor;
void main() {
${SAMPLE_GLSL}
${CLASS_GLSL}
  if (cover < 1e-4) discard;

  // A mean of *indices* is a mean of values: \`quantize\` is affine, so
  // dequantising first would be arithmetic that changes nothing and a second
  // place for lo/hi to be wrong. Nothing in the draw path ever needs the value
  // in its own units — the manifest's decode rule exists for a reader.
  float index = sum / cover;
  vec3 rgb = texture(u_ramp, vec2((index + 0.5) / u_levels, 0.5)).rgb;

  // Alpha is the coverage, so the plate fades out over one texel at its
  // boundary rather than ending in a staircase — which is the thing
  // \`raster-resampling\` cannot express at any setting.
  float a = cover * u_opacity;

  // Premultiplied: MapLibre sets blendFunc(ONE, ONE_MINUS_SRC_ALPHA) before
  // calling a custom layer and documents it. Returning straight alpha draws
  // every plate too bright, which looks like an opacity bug and is not one.
  fragColor = vec4(rgb * a, a);
}`

/**
 * The largest texture this machine's driver will take, or null when it cannot
 * be asked.
 *
 * WebGL2 *guarantees* only 2048, and HRRR/NAM ship 2816 and NBM 3584. This is
 * not a new problem — the `image` source uploads the same plate today and
 * fails the same way — but a custom layer is the first thing here that can ask,
 * and one line removes a class of "blank on somebody's laptop" report that
 * today has no diagnosis.
 *
 * Asked once, off a 1x1 offscreen context that is released immediately, so it
 * is available at `load()` time where the width is actually chosen rather than
 * at `onAdd`, which is too late. Under jsdom there is no `webgl2` context at
 * all and this is null, which means "no ceiling" — today's behaviour.
 */
let maxTextureSizeCache: number | null | undefined
export function maxTextureSize(): number | null {
  if (maxTextureSizeCache !== undefined) return maxTextureSizeCache
  maxTextureSizeCache = null
  // No WebGL2 in this environment at all — jsdom, or a headless runner. There
  // is nothing to ask and nothing that could draw the answer either way; the
  // guard is here so a test run does not print a canvas warning per file.
  if (typeof WebGL2RenderingContext === 'undefined') return maxTextureSizeCache
  try {
    const canvas = document.createElement('canvas')
    canvas.width = 1
    canvas.height = 1
    const gl = canvas.getContext('webgl2')
    if (gl) {
      const size = gl.getParameter(gl.MAX_TEXTURE_SIZE)
      if (typeof size === 'number' && size > 0) maxTextureSizeCache = size
      gl.getExtension('WEBGL_lose_context')?.loseContext()
    }
  } catch {
    // A browser that refuses a second context, or no document at all. No
    // ceiling is the honest answer and is what every release before this did.
  }
  return maxTextureSizeCache
}

/** Tests only: the limit is a property of the machine, not of the session. */
export function _resetMaxTextureSize(): void {
  maxTextureSizeCache = undefined
}

interface GlState {
  program: WebGLProgram
  vao: WebGLVertexArrayObject
  positions: WebGLBuffer
  data: WebGLTexture
  ramp: WebGLTexture
  uniforms: Record<string, WebGLUniformLocation | null>
}

export class DataTextureLayer implements Layer {
  readonly id: string
  protected frames: TextureFrame[] = []
  protected spec: DataTextureSpec | null = null
  private map: MapLibreMap | null = null
  protected current = 0
  private opacity: number
  private visible = true
  private loaded: Promise<void> | null = null
  private warmed = new Set<string>()
  private credit: string

  private gl: GlState | null = null
  /** Decoded and waiting for a frame to upload it on. Never uploaded here. */
  private pending: GrayImage | null = null
  private pendingSize: [number, number] = [0, 0]
  private pendingUrl = ''
  /** Monotonic, bumped by every `setTime`; a decode that loses is dropped. */
  private token = 0
  private uploaded = -1
  /** False until a texture is up, and again after `onAdd` builds a new one. */
  private hasTexture = false
  /**
   * The frame on the GPU, decoded, and which frame it is. Kept for a reader
   * that wants the index under a point — the radar map's tap readout — at
   * the one plate's worth of memory the upload already needed.
   */
  protected shown: { url: string; image: GrayImage } | null = null
  private rampDirty = true

  constructor(private readonly opts: DataTextureOptions) {
    this.id = opts.id
    this.opacity = opts.opacity
    this.credit = opts.attribution
  }

  get times(): readonly Date[] {
    return this.frames.map((f) => f.time)
  }

  currentTime(): Date | null {
    return this.frames[this.current]?.time ?? null
  }

  /** Who to credit. `Legend.svelte` prints it, because a custom layer has no
   *  source for `AttributionControl` to read one off. */
  ownAttribution(): string {
    return this.credit
  }

  /** Where the frames come from: the options' loader, or a subclass's own. */
  protected loadFrames(): Promise<DataTextureSpec | null> {
    return this.opts.loadFrames?.() ?? Promise.resolve(null)
  }

  load(): Promise<void> {
    this.loaded ??= this.loadFrames()
      .then((found) => {
        if (!found || found.frames.length === 0) {
          // Empty rather than an error. The controller mounts the layer either
          // way and it simply draws nothing.
          this.frames = []
          this.spec = null
          return
        }
        this.spec = found
        this.frames = [...found.frames].sort((a, b) => a.time.getTime() - b.time.getTime())
        if (found.attribution) this.credit = found.attribution
        this.current = 0
        this.rampDirty = true
      })
      .catch((e) => {
        // A degraded path a user is meant to notice gets a console line at
        // least: an empty model overlay is indistinguishable from a quiet day.
        console.warn(`${this.id}: could not load model frames`, e)
        this.frames = []
        this.spec = null
      })
    return this.loaded
  }

  private get key(): string {
    return `wx:${this.id}`
  }

  private custom(): CustomLayerInterface {
    return {
      id: this.key,
      type: 'custom',
      renderingMode: '2d',
      onAdd: (_map: MapLibreMap, gl: WebGL2RenderingContext) => this.onAdd(gl),
      onRemove: (_map: MapLibreMap, gl: WebGL2RenderingContext) => this.onRemove(gl),
      render: (gl: WebGL2RenderingContext, options: CustomRenderMethodInput) =>
        this.draw(gl, options),
    }
  }

  add(map: MapLibreMap, beforeId?: string): void {
    this.map = map
    if (!this.spec || this.frames.length === 0) return
    if (map.getLayer(this.key)) return
    map.addLayer(this.custom(), beforeId)
    // The first frame has to be decoded like any other; `onAdd` only builds
    // the GL objects.
    this.fetchFrame(this.current)
    this.prefetch()
  }

  remove(map: MapLibreMap): void {
    // `removeLayer` calls `onRemove`, which is where the GL objects go.
    if (map.getLayer(this.key)) map.removeLayer(this.key)
    if (this.map === map) this.map = null
    this.pending = null
  }

  setOpacity(opacity: number): void {
    this.opacity = opacity
    this.map?.triggerRepaint()
  }

  /**
   * Swap the frame list in place, keeping the texture on screen until the
   * frame now at the cursor has been fetched: for a layer whose frames move
   * while it is up (the radar map, every two minutes), where `reload()`'s
   * drop-and-recreate would blank the plate. The box must not move: the quad
   * is built once, in `onAdd`. The controller's next `setTime` fetches.
   */
  protected replaceSpec(found: DataTextureSpec): void {
    const same = (a: Uint8Array, b: Uint8Array) =>
      a.length === b.length && a.every((v, i) => v === b[i])
    if (this.spec && !same(this.spec.ramp, found.ramp)) this.rampDirty = true
    this.spec = found
    this.frames = [...found.frames].sort((a, b) => a.time.getTime() - b.time.getTime())
    // No frame is current until the controller says which instant to show.
    this.current = -1
    this.uploaded = -1
  }

  setTime(time: Date): void {
    const next = nearestIndex(
      this.frames.map((f) => f.time),
      time,
    )
    if (next < 0 || next === this.current) return
    this.current = next
    this.fetchFrame(next)
    this.prefetch()
  }

  setVisible(visible: boolean): void {
    if (this.visible === visible) return
    this.visible = visible
    // A flag the render method returns on, rather than
    // `setLayoutProperty(id, 'visibility', …)`. That does appear to reach a
    // custom layer through `StyleLayer.isHidden`, but a flag is true by
    // construction and needs no belief about a library internal. It also stops
    // `prefetch()`, which is the rule that already makes opacity-0 mean *off*.
    this.map?.triggerRepaint()
    if (visible) this.prefetch()
  }

  /**
   * Resolves when the current frame is uploaded *and* the map has gone idle.
   *
   * `once('idle')` alone is not enough any more: MapLibre knows nothing about
   * our decode, so a screenshot could land on an empty quad.
   */
  ready(): Promise<void> {
    const map = this.map
    if (!map || this.frames.length === 0) return Promise.resolve()
    const idle = new Promise<void>((resolve) => map.once('idle', () => resolve()))
    return this.drawn().then(() => idle)
  }

  private drawn(): Promise<void> {
    if (this.uploaded === this.current) return Promise.resolve()
    return new Promise((resolve) => {
      const deadline = Date.now() + 10_000
      const poll = () => {
        if (this.uploaded === this.current || Date.now() > deadline) resolve()
        else setTimeout(poll, 50)
      }
      poll()
    })
  }

  /**
   * Fetch and decode one frame, and queue it for the next `render`.
   *
   * **Decoded by `decodePlate`, never by the browser's image decoder.** WebKit
   * colour-manages a gray PNG on the way through `createImageBitmap` whatever
   * the options say, and dithers 12% of a real plate by ±1 — which turns the
   * reserved index into the top of the ramp (Phase 8 plan §9.7, and `plate.ts`
   * has the numbers). Bytes in, bytes out, off the main thread.
   *
   * A decode that resolves after a newer `setTime` is dropped by the token:
   * `setTime` can fire faster than a 4 MB plate decodes, especially on the
   * first pass of a loop, and a stale frame uploaded over a newer one shows the
   * wrong hour with nothing on screen to say so.
   */
  private fetchFrame(index: number): void {
    const frame = this.frames[index]
    if (!frame || typeof fetch !== 'function') return
    const mine = ++this.token
    void fetch(frame.url)
      .then((r) => (r.ok ? r.arrayBuffer() : Promise.reject(new Error(`${frame.url} ${r.status}`))))
      .then((bytes) => decodePlate(bytes))
      .then((image) => {
        if (mine !== this.token) return
        this.pending = image
        this.pendingUrl = frame.url
        this.pendingSize = [image.width, image.height]
        this.map?.triggerRepaint()
      })
      .catch((e) => {
        console.warn(`${this.id}: could not decode a model plate`, e)
      })
  }

  /**
   * Warm the frames around the cursor into the browser's HTTP cache.
   *
   * **Bytes, not bitmaps.** Unlike `GeoJsonFramesLayer`, which keeps parsed
   * documents inside its window, nothing decoded is kept here: a decoded plate
   * is 4-8 MB and the saving already lives in the HTTP cache, because the URLs
   * are `immutable`. So this window bounds *bandwidth* where that one bounds
   * memory, and the difference is worth the sentence in both files.
   */
  private prefetch(): void {
    if (!this.visible || typeof fetch !== 'function') return
    const from = Math.max(0, this.current - (this.opts.prefetchBehind ?? DEFAULT_BEHIND))
    const to = Math.min(
      this.frames.length - 1,
      this.current + (this.opts.prefetchAhead ?? DEFAULT_AHEAD),
    )
    for (let i = from; i <= to; i++) {
      const { url } = this.frames[i]
      if (this.warmed.has(url)) continue
      this.warmed.add(url)
      void fetch(url).catch(() => this.warmed.delete(url))
    }
  }

  // ---------- GL, and nothing outside these three ----------

  private onAdd(gl: WebGL2RenderingContext): void {
    const program = linkProgram(gl, VERTEX_SOURCE, FRAGMENT_SOURCE)
    if (!program || !this.spec) return

    const corners = mercatorQuad(this.spec.bbox3857)
    // Two triangles over the plate's box. `a_uv` is 0..1 with v running down,
    // which matches the PNG's own row order — the texture is uploaded with
    // `UNPACK_FLIP_Y_WEBGL` off, so row 0 of the image is v = 0.
    const [tl, tr, br, bl] = corners
    const vertices = new Float32Array([
      tl[0], tl[1], 0, 0,
      tr[0], tr[1], 1, 0,
      bl[0], bl[1], 0, 1,
      tr[0], tr[1], 1, 0,
      br[0], br[1], 1, 1,
      bl[0], bl[1], 0, 1,
    ])

    const positions = gl.createBuffer()
    const vao = gl.createVertexArray()
    gl.bindVertexArray(vao)
    gl.bindBuffer(gl.ARRAY_BUFFER, positions)
    gl.bufferData(gl.ARRAY_BUFFER, vertices, gl.STATIC_DRAW)
    const posLoc = gl.getAttribLocation(program, 'a_pos')
    const uvLoc = gl.getAttribLocation(program, 'a_uv')
    gl.enableVertexAttribArray(posLoc)
    gl.vertexAttribPointer(posLoc, 2, gl.FLOAT, false, 16, 0)
    gl.enableVertexAttribArray(uvLoc)
    gl.vertexAttribPointer(uvLoc, 2, gl.FLOAT, false, 16, 8)
    gl.bindVertexArray(null)

    this.gl = {
      program,
      vao,
      positions,
      // `R8`, single channel: a quarter of the texture memory, which on NBM's
      // 3584 px plate is 8.1 MB against 32.6. An `image` source cannot do this
      // and a custom layer can.
      data: newTexture(gl),
      ramp: newTexture(gl),
      uniforms: Object.fromEntries(
        [
          'u_matrix',
          'u_data',
          'u_ramp',
          'u_size',
          'u_nodata',
          'u_split',
          'u_opacity',
          'u_levels',
        ].map((name) => [name, gl.getUniformLocation(program, name)]),
      ),
    }
    this.uploaded = -1
    this.hasTexture = false
    this.shown = null
    this.rampDirty = true
  }

  private onRemove(gl: WebGL2RenderingContext): void {
    const state = this.gl
    this.gl = null
    if (!state) return
    gl.deleteProgram(state.program)
    gl.deleteVertexArray(state.vao)
    gl.deleteBuffer(state.positions)
    gl.deleteTexture(state.data)
    gl.deleteTexture(state.ramp)
  }

  private draw(gl: WebGL2RenderingContext, options: CustomRenderMethodInput): void {
    const state = this.gl
    const spec = this.spec
    if (!state || !spec || !this.visible) return

    if (this.rampDirty) {
      gl.bindTexture(gl.TEXTURE_2D, state.ramp)
      // 255 x 3 bytes a row is not a multiple of four, which is the default
      // unpack alignment and would shear the ramp by a byte per row.
      gl.pixelStorei(gl.UNPACK_ALIGNMENT, 1)
      const levels = spec.ramp.length / 3
      gl.texImage2D(
        gl.TEXTURE_2D, 0, gl.RGB8, levels, 1, 0, gl.RGB, gl.UNSIGNED_BYTE, spec.ramp,
      )
      // The ramp is the one texture that *is* filtered by the hardware:
      // sampling at (index + 0.5) / levels hits texel centres at integer
      // indices and interpolates between them, which is what `np.interp` does
      // one stage earlier on the Python side.
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR)
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR)
      this.rampDirty = false
    }

    if (this.pending) {
      const image = this.pending
      this.pending = null
      gl.bindTexture(gl.TEXTURE_2D, state.data)
      gl.pixelStorei(gl.UNPACK_FLIP_Y_WEBGL, false)
      gl.pixelStorei(gl.UNPACK_PREMULTIPLY_ALPHA_WEBGL, false)
      // A plate row is `width` bytes, which is rarely a multiple of four.
      gl.pixelStorei(gl.UNPACK_ALIGNMENT, 1)
      // From a typed array, which no engine colour-manages: the texel is the
      // byte `encode_gray` wrote.
      gl.texImage2D(
        gl.TEXTURE_2D, 0, gl.R8, image.width, image.height, 0, gl.RED, gl.UNSIGNED_BYTE,
        image.data,
      )
      this.uploaded = this.current
      this.hasTexture = true
      this.shown = { url: this.pendingUrl, image }
    }
    if (!this.hasTexture) return

    gl.useProgram(state.program)
    // The typings say `mat4`, and MapLibre carries both `Mat4f32` and
    // `Mat4f64`; `uniformMatrix4fv` wants floats.
    gl.uniformMatrix4fv(
      state.uniforms.u_matrix,
      false,
      new Float32Array(options.defaultProjectionData.mainMatrix as ArrayLike<number>),
    )
    gl.activeTexture(gl.TEXTURE0)
    gl.bindTexture(gl.TEXTURE_2D, state.data)
    gl.uniform1i(state.uniforms.u_data, 0)
    gl.activeTexture(gl.TEXTURE1)
    gl.bindTexture(gl.TEXTURE_2D, state.ramp)
    gl.uniform1i(state.uniforms.u_ramp, 1)
    gl.uniform2i(state.uniforms.u_size, this.pendingSize[0], this.pendingSize[1])
    gl.uniform1f(state.uniforms.u_nodata, spec.nodata)
    gl.uniform1f(state.uniforms.u_split, spec.split ?? spec.nodata)
    gl.uniform1f(state.uniforms.u_opacity, this.opacity)
    gl.uniform1f(state.uniforms.u_levels, spec.ramp.length / 3)
    gl.bindVertexArray(state.vao)
    gl.drawArrays(gl.TRIANGLES, 0, 6)
    gl.bindVertexArray(null)
  }
}

function newTexture(gl: WebGL2RenderingContext): WebGLTexture {
  const texture = gl.createTexture()
  gl.bindTexture(gl.TEXTURE_2D, texture)
  // `NEAREST` on the *data* texture is not a filtering choice: the shader does
  // its own, validity-weighted, so the hardware must hand back the raw texel.
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.NEAREST)
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.NEAREST)
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE)
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE)
  return texture
}

function linkProgram(
  gl: WebGL2RenderingContext,
  vertexSource: string,
  fragmentSource: string,
): WebGLProgram | null {
  const vertex = compile(gl, gl.VERTEX_SHADER, vertexSource)
  const fragment = compile(gl, gl.FRAGMENT_SHADER, fragmentSource)
  if (!vertex || !fragment) return null
  const program = gl.createProgram()
  gl.attachShader(program, vertex)
  gl.attachShader(program, fragment)
  gl.linkProgram(program)
  gl.deleteShader(vertex)
  gl.deleteShader(fragment)
  if (!gl.getProgramParameter(program, gl.LINK_STATUS)) {
    // Loud, because the alternative is a blank map with a green test suite —
    // which is the failure this repo has already lived through once.
    console.error('data texture: program link failed', gl.getProgramInfoLog(program))
    gl.deleteProgram(program)
    return null
  }
  return program
}

function compile(gl: WebGL2RenderingContext, type: number, source: string): WebGLShader | null {
  const shader = gl.createShader(type)
  if (!shader) return null
  gl.shaderSource(shader, source)
  gl.compileShader(shader)
  if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) {
    console.error('data texture: shader compile failed', gl.getShaderInfoLog(shader))
    gl.deleteShader(shader)
    return null
  }
  return shader
}
